import { useCallback, useEffect, useState } from 'react'
import { NumericTextBox } from '@progress/kendo-react-inputs'

import { EmployeeUniform, getEmployeeUniform, setEmployeeUniformAllowances } from '../../data/uniformData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import { RequestLines, StatusPill, formatDate } from './UniformPage'
import './Uniform.css'

/**
 * One person's uniform, on the Users screen: this year's allowance for each
 * item (their own, or the item's default), where they are against it, and every
 * request they have made.
 */
const EmployeeUniformPanel = ({ employeeId }: { employeeId: number }) => {
  const { showToast } = useToast()
  const [uniform, setUniform] = useState<EmployeeUniform | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Their own allowance per item; null means "the item's default".
  const [overrides, setOverrides] = useState<Record<number, number | null>>({})
  const [saving, setSaving] = useState(false)

  const apply = (u: EmployeeUniform) => {
    setUniform(u)
    setOverrides(Object.fromEntries(u.items.map((i) => [i.uniformItemId, i.allowanceOverride])))
  }

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      apply(await getEmployeeUniform(employeeId))
    } catch (error) {
      setLoadError(getApiErrorDetails(error, "Their uniform couldn't be loaded.").message)
    }
  }, [employeeId])

  useEffect(() => {
    load()
  }, [load])

  if (loadError) return <div className='uni-panel'><div className='uni-error'>{loadError}</div></div>
  if (!uniform) return <div className='uni-panel uni-empty'>Loading uniform…</div>

  const dirty = uniform.items.some((i) => (overrides[i.uniformItemId] ?? null) !== i.allowanceOverride)

  const save = async () => {
    setSaving(true)
    try {
      apply(
        await setEmployeeUniformAllowances(
          employeeId,
          uniform.items.map((i) => ({ uniformItemId: i.uniformItemId, quantity: overrides[i.uniformItemId] ?? null }))
        )
      )
      showToast('Uniform allowances saved', 'success')
    } catch (error) {
      showToast(getApiErrorDetails(error, "The allowances couldn't be saved.").message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='uni-panel' style={{ padding: '1rem' }}>
      <section className='uni-panel__section'>
        <div className='uni-panel__heading'>
          <div>
            <h3>Allowances</h3>
            <span className='uni-muted'>
              {formatDate(uniform.yearStart)} to {formatDate(uniform.yearEnd)}. Leave a box empty to use the item's
              default; 0 means they can't request it.
            </span>
          </div>
          <button type='button' className='uni-btn uni-btn--primary' onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save allowances'}
          </button>
        </div>

        {uniform.items.length === 0 ? (
          <div className='uni-empty'>No uniform items have been set up yet.</div>
        ) : (
          <table className='uni-table'>
            <thead>
              <tr>
                <th>Item</th>
                <th className='uni-num'>Default</th>
                <th className='uni-num'>Their allowance</th>
                <th className='uni-num'>Used</th>
                <th className='uni-num'>On order</th>
                <th className='uni-num'>Left</th>
                <th>Last requested</th>
              </tr>
            </thead>
            <tbody>
              {uniform.items.map((i) => (
                <tr key={i.uniformItemId}>
                  <td>
                    <span style={{ fontWeight: 600 }}>{i.name}</span>
                    {!i.isOffered && (
                      <div className='uni-muted'>
                        {i.inItemGroups ? 'Excluded' : "Not offered - not in the item's groups"}
                      </div>
                    )}
                  </td>
                  <td className='uni-num'>{i.defaultAllowance}</td>
                  <td className='uni-num'>
                    <NumericTextBox
                      disabled={!i.inItemGroups}
                      value={overrides[i.uniformItemId] ?? null}
                      placeholder={`${i.defaultAllowance}`}
                      min={0}
                      format='n0'
                      width={90}
                      onChange={(e) =>
                        setOverrides((prev) => ({
                          ...prev,
                          [i.uniformItemId]: e.value == null ? null : Math.max(0, Math.round(e.value)),
                        }))
                      }
                    />
                  </td>
                  <td className='uni-num'>{i.used}</td>
                  <td className='uni-num'>{i.outstanding}</td>
                  <td className={`uni-num${i.available === 0 ? ' uni-left--none' : ''}`}>{i.available}</td>
                  <td>
                    {formatDate(i.lastRequestedDate)}
                    {i.lastSize && <span className='uni-muted'> ({i.lastSize})</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className='uni-panel__section'>
        <div className='uni-panel__heading'>
          <h3>Request history</h3>
        </div>
        {uniform.requests.length === 0 ? (
          <div className='uni-empty'>No uniform requested yet.</div>
        ) : (
          <table className='uni-table'>
            <thead>
              <tr>
                <th>Requested</th>
                <th>Items</th>
                <th>Status</th>
                <th className='uni-num'>Cost</th>
              </tr>
            </thead>
            <tbody>
              {uniform.requests.map((r) => (
                <tr key={r.uniformRequestId}>
                  <td>
                    {formatDate(r.requestedDate)}
                    {r.requestedByName && r.requestedByEmployeeId !== r.employeeId && (
                      <div className='uni-muted'>by {r.requestedByName}</div>
                    )}
                  </td>
                  <td>
                    <RequestLines request={r} />
                  </td>
                  <td>
                    <StatusPill status={r.status} />{' '}
                    {r.outsideAllowance && <span className='uni-pill uni-pill--outside'>Outside allowance</span>}
                    {r.status === 'Delivered' && <div className='uni-muted'>Delivered {formatDate(r.deliveredDate)}</div>}
                    {r.statusReason && <div className='uni-muted'>{r.statusReason}</div>}
                  </td>
                  <td className='uni-num'>{r.totalCost != null ? `£${r.totalCost.toFixed(2)}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  )
}

export default EmployeeUniformPanel
